import { Assert } from '@cotera/utilities';
import { BaseModel } from '../actions/framework';
import { DG } from '@cotera/sdk/core';
import { TargetableModel } from '../actions/framework/types';
import {
  ReadonlyWatchable,
  StaleState,
  Watchable,
} from '../actions/framework/watchable';
import { err, ok, Result } from 'neverthrow';
import { v4 } from 'uuid';

import { Routes } from '../routes';
import { Ty } from '@cotera/era';
import { uniq } from 'lodash';
import { loadAgentChatId } from './agent-chat-state';
import { LlmModels } from '@cotera/api';

export class AgentModel extends BaseModel implements TargetableModel {
  private _isTargeted: boolean = false;

  readonly formState: Watchable<{
    agentState: DG.DocumentEditor.DocumentState;
    model: string;
    schedule: 'never' | 'daily' | 'hourly' | { cron: string };
  }>;

  readonly currentTab: Watchable<string>;
  readonly errors: ReadonlyWatchable<DG.Graph.GraphError[]>;
  readonly isEditorStale: ReadonlyWatchable<StaleState>;
  readonly hasBeenTargeted: Watchable<boolean>;
  readonly changes: ReadonlyWatchable<Record<string, boolean>>;
  readonly internalCompiler: ReadonlyWatchable<DG.GraphCompiler>;
  readonly graph: ReadonlyWatchable<DG.BuildExplorer>;
  readonly newOperations = Watchable.fromValue<DG.OperationData[]>([]);
  readonly storedFormState: ReadonlyWatchable<{
    agentState: DG.DocumentEditor.DocumentState;
    model: string;
    schedule: 'never' | 'daily' | 'hourly' | { cron: string };
  }>;
  readonly pageTitle: ReadonlyWatchable<string>;
  readonly hasMessages: Watchable<boolean>;
  readonly showSuggestions: ReadonlyWatchable<boolean>;
  /**
   * the UI name state - editable by the user
   */
  readonly editorState: ReadonlyWatchable<DG.DocumentEditor.EditorState>;
  readonly route: ReadonlyWatchable<string>;
  /**
   * The saved name in the base graph - updated when operations are staged
   * This is the source of truth for the agent's name in the graph
   */
  readonly currentNameInGraph: Watchable<string | null>;
  readonly name: ReadonlyWatchable<string>;
  /**
   * the identity in the graph - null if not exists yet
   */
  readonly agentIdentity: ReadonlyWatchable<DG.Graph.Identity | null>;
  readonly promptInput: ReadonlyWatchable<{
    systemMessage: string;
    toolIds: string[];
    outputShape: Ty.ExtendedAttributeType;
    model: string;
  }>;
  readonly chatId: Watchable<string>;
  readonly needsMessageRestore: Watchable<boolean>;

  constructor(
    private readonly inputs: {
      name: string;
      graph: ReadonlyWatchable<DG.BuildExplorer>;
      compiler: ReadonlyWatchable<DG.GraphCompiler>;
    },
    readonly opts: {
      id?: string;
      targeted?: boolean;
      isNew?: boolean;
    } = {}
  ) {
    super(opts.id ?? inputs.name);

    this._isTargeted = opts.targeted ?? false;

    this.storedFormState = Watchable.from((get) => {
      const agentNode = get(inputs.graph).nodeMap[inputs.name];
      if (opts.isNew || agentNode === undefined) {
        return {
          agentState: { type: 'doc' as const, content: [] },
          model: LlmModels.azureFoundry.AzureGpt5Dot5,
          schedule: 'never' as const,
        };
      }
      Assert.assert(agentNode.t === 'agent');
      return {
        agentState: agentNode.editorState ?? {
          type: 'doc' as const,
          content: [],
        },
        model: agentNode.model ?? LlmModels.azureFoundry.AzureGpt5Dot5,
        schedule: agentNode.schedule ?? ('never' as const),
      };
    });

    this.formState = Watchable.fromValue(this.storedFormState.snapshot());

    this.hasMessages = Watchable.fromValue(false);

    this.editorState = Watchable.from((get) => {
      return new DG.DocumentEditor.EditorState(get(this.formState).agentState);
    });

    // Initialize currentNameInGraph with the name from the base graph
    // This will be updated when operations are staged via markAsStaged()
    this.currentNameInGraph = Watchable.fromValue<string | null>(
      opts.isNew ? null : inputs.name
    );

    this.name = Watchable.from((get) => {
      return get(this.editorState).title() ?? 'New Agent';
    });

    this.pageTitle = Watchable.from((get) => {
      return get(this.name);
    });

    this.route = Watchable.from((get) => {
      return Routes.agent.forId(get(this.name)).toString();
    });

    this.internalCompiler = Watchable.from((get) => {
      const baseCompiler = get(inputs.compiler);

      return baseCompiler.commit({
        actorId: v4(),
        message: null,
        operations: [...get(this.newOperations)],
        timestamp: new Date(),
      });
    });

    this.showSuggestions = Watchable.from((get) => {
      return !get(this.editorState).hasBodyContent();
    });

    this.currentTab = Watchable.fromValue(
      opts.isNew === true ? 'edit-and-chat' : 'chat'
    );

    this.graph = Watchable.from((get) => {
      return get(this.internalCompiler)
        .compile()
        .match(
          (x) => x,
          (e) => e.lastGoodBuild
        );
    });

    this.agentIdentity = Watchable.from((get) => {
      const savedName = get(this.currentNameInGraph);
      if (savedName === null) {
        return null;
      }

      // Look up in the BASE graph (without new operations) using the saved name
      const baseGraph = get(inputs.graph);
      const agentNode = baseGraph.nodeMap[savedName];

      if (agentNode === undefined) {
        return null;
      }
      if (agentNode.t !== 'agent') {
        return null;
      }
      return agentNode.meta.identity;
    });

    this.errors = Watchable.from((get) => {
      const sym = get(this.graph).forSymbol(get(this.name));

      if (sym === null) {
        return [];
      }

      return sym.builtNode.match(
        () => [],
        (e) => [e.err]
      );
    });

    this.hasBeenTargeted = Watchable.fromValue(false);

    this.changes = Watchable.from((get) => {
      const editorState = get(this.editorState);
      const initialAgentState = new DG.DocumentEditor.EditorState(
        get(this.storedFormState).agentState
      );
      const graph = get(inputs.graph);

      const currentName = get(this.currentNameInGraph);
      const formName = get(this.name);
      const formState = get(this.formState);
      const storedFormState = get(this.storedFormState);

      return {
        name: currentName !== null && currentName !== formName,
        prompt:
          editorState.toPromptMarkdown(graph) !==
          initialAgentState.toPromptMarkdown(graph),
        model: formState.model !== storedFormState.model,
        outputShape:
          Ty.displayTy(editorState.getOutputTy()) !==
          Ty.displayTy(initialAgentState.getOutputTy()),
        schedule:
          JSON.stringify(formState.schedule) !==
          JSON.stringify(storedFormState.schedule),
      };
    });

    this.promptInput = Watchable.from((get) => {
      const editorState = get(this.editorState);
      const graph = get(inputs.graph);

      const prompt = editorState.toPromptMarkdown(graph);
      const builtNodes = graph.builtNodes();

      const toolIds = uniq(
        editorState
          .getMentions(graph)
          .filter((mention) => mention.type === 'tool')
          .map((mention) => {
            // Look up the tool in the graph by name (mention.id)
            const toolNode = builtNodes[mention.id];
            if (toolNode?.item.t === 'tool') {
              return toolNode.item.def.toolId;
            }

            // Fallback: use referenceId for tools-v2 tools that aren't in the graph
            // Tools from tools-v2 have their key stored in referenceId
            if (mention.referenceId) {
              return mention.referenceId;
            }

            return undefined;
          })
          .filter((id) => id !== undefined)
      );

      const outputShape = editorState.getOutputTy();
      const model =
        get(this.formState).model ?? LlmModels.azureFoundry.AzureGpt5Dot5;

      return {
        systemMessage: prompt,
        toolIds,
        outputShape,
        model,
      };
    });

    // Check for preserved chatId from navigation (messages will be fetched from server)
    const preservedChatId = loadAgentChatId(inputs.name);

    if (preservedChatId !== null) {
      this.chatId = Watchable.fromValue(preservedChatId);
      this.needsMessageRestore = Watchable.fromValue(true);
    } else {
      this.chatId = Watchable.fromValue(v4());
      this.needsMessageRestore = Watchable.fromValue(false);
    }
  }

  get isTargeted() {
    return this._isTargeted;
  }

  markAsStaged(): void {
    // Update the saved name to the current name now that changes are staged
    this.currentNameInGraph.set(this.name.snapshot());
    // Clear new operations since they've been staged
    this.newOperations.set([]);
  }

  target(): void {
    this._isTargeted = true;
    this.hasBeenTargeted.set(true);
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  compile(
    props: {
      setOperations?: boolean;
    } = { setOperations: true }
  ): Result<
    { t: 'agent' },
    | { t: 'build'; e: DG.CommitsBuildFailure | { t: string; message: string } }
    | { t: 'tc'; e: DG.Graph.GraphError }
  > {
    const graphSnapshot = this.inputs.graph.snapshot();
    const compiler = graphSnapshot.compiler;

    const nextOps = this.makeOpsFromState();

    if (nextOps.isErr()) {
      return err({ t: 'build', e: nextOps.error });
    }

    const compilationResult = compiler
      .commit({
        actorId: v4(),
        message: null,
        operations: [...nextOps.value],
        timestamp: new Date(),
      })
      .compile();

    if (compilationResult.isErr()) {
      return err({ t: 'build', e: compilationResult.error.err });
    }

    // Allow saving broken agents - users should be able to save work-in-progress
    // The errors will be shown in the UI via the errors watchable

    if (props.setOperations) {
      this.newOperations.set(nextOps.value);
    }

    return ok({
      t: 'agent',
    });
  }

  private makeOpsFromState(): Result<
    DG.OperationData[],
    { t: string; message: string }
  > {
    const name = this.name.snapshot();
    const { agentState, model, schedule } = this.formState.snapshot();

    if (this.agentIdentity.snapshot() === null) {
      const editorState = new DG.DocumentEditor.EditorState(agentState);

      // Create the agent node
      return ok([
        DG.Operations.createAgent({
          symbol: name,
          outputTy: Ty.displayTy(editorState.getOutputTy()),
          editorState: agentState,
          model: model,
          schedule: schedule,
        }),
      ]);
    }

    const ops: DG.OperationData[] = [...this.newOperations.snapshot()];

    const nameHasChanged = name !== this.currentNameInGraph.snapshot();

    // TODO: implement proper agent change detection
    // For now, always update when we're saving
    const agentHasChanged = true;

    const agentSymbol = this.graph
      .snapshot()
      .forSymbol(this.currentNameInGraph.snapshot()!);

    if (agentSymbol === null) {
      return err({
        t: 'agent-not-found',
        message: `Agent not found: ${this.currentNameInGraph.snapshot()!}`,
      });
    }

    if (nameHasChanged && !this.isNew) {
      const renameOps = agentSymbol.makeRenameOps({
        to: name,
      });

      if (renameOps.isErr()) {
        return err({
          t: 'rename-failure',
          message: JSON.stringify(renameOps.error),
        });
      }
      ops.push(...renameOps.value);
    }

    if (agentHasChanged) {
      const editorState = new DG.DocumentEditor.EditorState(agentState);

      const updateOps = agentSymbol.makeUpdateOps({
        symbol: name, // Use the NEW name (after rename if applicable)
        outputTy: Ty.displayTy(editorState.getOutputTy()),
        editorState: agentState,
        model: model,
        schedule: schedule,
      });

      if (updateOps.isErr()) {
        return err({ t: 'update-failure', message: updateOps.error });
      }

      ops.push(...updateOps.value);
    }

    return ok(ops);
  }

  get isNew() {
    return this.currentNameInGraph.snapshot() === null;
  }

  isEqualTo(other: AgentModel) {
    return (
      this.agentIdentity.snapshot()?.toString() ===
      other.agentIdentity.snapshot()?.toString()
    );
  }
}
